import type { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import { STATUS_ICONS } from 'modules/course/consts/assignment-status-icons'
import useStepRouter from 'modules/course/hooks/use-step-router'
import type { CourseMenuLectureInterface } from 'modules/course/types/course-menu-interface'
import { CourseLectureAssignmentStatusEnum } from 'modules/course/types/course-menu-interface'
import { AssignmentsStatusTranslations } from '../../course-lecture-assignment/constants'
import { getAssignmentStatusTextColor } from '../../course-lecture-assignment/utils'

interface CourseLectureAssignmentStatusProps {
  lecture: CourseMenuLectureInterface
}

export const CourseLectureAssignmentStatus = ({
  lecture,
}: CourseLectureAssignmentStatusProps): ReactNode => {
  const { t } = useTranslation()
  const { getAssignmentUrl, isCurrentAssignment } = useStepRouter()

  const isAssignmentCurrent = isCurrentAssignment(lecture)
  const currentStatus = lecture.assignment?.status ?? CourseLectureAssignmentStatusEnum.NotSubmitted
  const isAvailable = lecture.available
  const StatusIcon = STATUS_ICONS[currentStatus]

  const textColorClass = !isAvailable
    ? 'cursor-default text-THEME-disabled'
    : isAssignmentCurrent
      ? 'text-THEME-secondary hover:text-THEME-hover-secondary'
      : 'text-THEME-primary hover:text-THEME-hover-primary'

  return (
    <LinkWithoutPrefetch
      href={getAssignmentUrl(lecture.id)}
      disabled={!isAvailable}
      className={twMerge('mt-3 ml-6.5 flex items-center', textColorClass)}
    >
      <div className="flex size-4 shrink-0 items-center justify-center">
        <StatusIcon className="size-full" />
      </div>
      <span className="mr-1.5 ml-1">{t('course.lecture.assignment_text')}</span>
      {!!lecture.assignment?.status && (
        <p>
          <span>•</span>
          <span
            className={twJoin('ml-1.5', getAssignmentStatusTextColor(lecture.assignment.status))}
          >
            {t(AssignmentsStatusTranslations[lecture.assignment.status])}
          </span>
        </p>
      )}
    </LinkWithoutPrefetch>
  )
}
